import { createReducer, on, Action } from '@ngrx/store';

import * as CommonActions from './common.actions';

export const COMMON_FEATURE_KEY = 'common';

export interface State {
  editMode: boolean;
  defaultFilters: { [url: string]: any };
}

export interface CommonPartialState {
  readonly [COMMON_FEATURE_KEY]: State;
}

const getStoreKey = (): string => {
  const selectedLayout = localStorage.getItem('selected_layout');
  return selectedLayout + '.' + COMMON_FEATURE_KEY;
};

const loadStateFromLocalStorage = (): State => {
  try {
    const localState = localStorage.getItem(getStoreKey());
    if (localState === null) {
      return {
        editMode: true,
        defaultFilters: {}
      };
    }
    const parsedState = JSON.parse(localState);
    // Parse dates in defaultFilters
    for (const url in parsedState.defaultFilters) {
      if (parsedState.defaultFilters.hasOwnProperty(url)) {
        const filter = parsedState.defaultFilters[url];
        if (filter.createdAfter) {
          filter.createdAfter = new Date(filter.createdAfter);
        }
        if (filter.createdBefore) {
          filter.createdBefore = new Date(filter.createdBefore);
        }
        if (filter.updatedAfter) {
          filter.updatedAfter = new Date(filter.updatedAfter);
        }
        if (filter.updatedBefore) {
          filter.updatedBefore = new Date(filter.updatedBefore);
        }
      }
    }
    return parsedState;
  } catch (error) {
    console.error('Could not load state from localStorage', error);
    return undefined;
  }
};

const saveStateToLocalStorage = (state: State): void => {
  try {
    const stateToSave = {
      ...state,
      defaultFilters: { ...state.defaultFilters }
    };
    // Convert dates to strings in defaultFilters
    for (const url in stateToSave.defaultFilters) {
      if (stateToSave.defaultFilters.hasOwnProperty(url)) {
        const filter = { ...stateToSave.defaultFilters[url] };
        if (filter.createdAfter) {
          filter.createdAfter = filter.createdAfter.toISOString();
        }
        if (filter.createdBefore) {
          filter.createdBefore = filter.createdBefore.toISOString();
        }
        if (filter.updatedAfter) {
          filter.updatedAfter = filter.updatedAfter.toISOString();
        }
        if (filter.updatedBefore) {
          filter.updatedBefore = filter.updatedBefore.toISOString();
        }
        stateToSave.defaultFilters[url] = filter;
      }
    }
    localStorage.setItem(getStoreKey(), JSON.stringify(stateToSave));
  } catch (error) {
    console.error('Could not save state to localStorage', error);
  }
};

export const initialState: State = loadStateFromLocalStorage();

const commonReducer = createReducer(
  initialState,
  on(CommonActions.setEditMode, (state, { editMode }) => ({
    ...state,
    editMode
  })),
  on(CommonActions.setDefaultFilter, (state, { url, defaultFilter }) => {
    const newState = {
      ...state,
      defaultFilters: { ...state.defaultFilters, [url]: defaultFilter }
    };
    saveStateToLocalStorage(newState);
    return newState;
  })
);

export function reducer(state: State | undefined, action: Action) {
  return commonReducer(state, action);
}
